/* global gtag */
import { isEmpty, isNil } from "ramda";

import { getContentTypeInFlow } from "tracking/shared";
import { appRoutes } from "routes";
import { jjLogger } from "utils/logUtils";
import {
  LOGIN_SIGNUP_METHODS,
  SHARE_METHODS,
  SHARE_ACTIONS,
  EDIT_SUBSCRIPTION_ACTIONS,
  UPLOAD_SOURCES,
  FOUND_ACTIONS,
  MORPHABLE_PACK_PREVIEW_ACTIONS,
  MORPHABLE_PACK_CONFIRMATION_ACTIONS,
  MORPHABLE_PACK_CONFIRMATION_ACTIONS_KEYS,
  TEMPLATE_GROUP_TYPES,
  VIEW_IMAGE_ACTIONS,
} from "enums";
import CategoryPresenter from "presenters/CategoryPresenter";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import TemplatePresenter from "presenters/TemplatePresenter";
import HomePagePresenter from "presenters/HomePagePresenter";
import MakePresenter from "presenters/MakePresenter";
import OfferPresenter from "presenters/OfferPresenter";
import PersonPresenter from "presenters/PersonPresenter";
import SubscriptionPresenter from "presenters/SubscriptionPresenter";
import UserPresenter from "presenters/UserPresenter";
import InvitePresenter from "presenters/InvitePresenter";
import MorphedPhotoPresenter from "presenters/MorphedPhotoPresenter";
import PagePresenter from "presenters/PagePresenter";

import { isMethodValid } from "../../validators/tracking";

const TRACK_LOGIN_AND_SIGNUP_METHODS = Object.values(LOGIN_SIGNUP_METHODS);
const TRACK_SHARE_METHODS = Object.values(SHARE_METHODS);
const TRACK_SHARE_ACTIONS = Object.values(SHARE_ACTIONS);
const TRACK_PHOTO_UPLOAD_SOURCES = Object.values(UPLOAD_SOURCES);
const TRACK_FOUND_ACTIONS = Object.values(FOUND_ACTIONS);
const TRACK_EDIT_SUBSCRIPTION_ACTIONS = Object.values(EDIT_SUBSCRIPTION_ACTIONS);
const TRACK_MORPHABLE_PACK_PREVIEW_ACTIONS = Object.values(MORPHABLE_PACK_PREVIEW_ACTIONS);
const TRACK_MORPHABLE_PACK_CONFIRMATION_ACTIONS = Object.values(MORPHABLE_PACK_CONFIRMATION_ACTIONS);
const TRACK_VIEW_IMAGE_ACTIONS = Object.values(VIEW_IMAGE_ACTIONS);

const trackEvent = (name, params) => {
  gtag("event", name, params);
};

const trackPhotoUploadEvent = (eventName, source, templateGroup) => {
  if (!TRACK_PHOTO_UPLOAD_SOURCES.includes(source)) {
    jjLogger.logError(`trackPhotoUpload: Invalid source: ${source}`);
    return;
  }

  const params = { face_photo_source: source };

  if (templateGroup) {
    params.item_id = TemplateGroupPresenter.id(templateGroup);
    params.content_type = TemplateGroupPresenter.templateGroupType(templateGroup);
  }

  trackEvent(eventName, params);
};

export const trackPhotoUpload = (source, templateGroup) => {
  trackPhotoUploadEvent("photo_upload", source, templateGroup);
};

export const trackPhotoUploadSuccess = (source, templateGroup) => {
  trackPhotoUploadEvent("photo_upload_success", source, templateGroup);
};

export const trackViewImageEvent = (targetPhotoSlug, morphablePackGroupSlug) => {
  if (isNil(targetPhotoSlug) || isNil(morphablePackGroupSlug)) {
    jjLogger.logError(
      `trackViewImageEvent: Required params not defined. Target photo slug: ${targetPhotoSlug} - morphablePackGroup slug: ${morphablePackGroupSlug}`,
    );
    return;
  }

  const params = {
    item_id: morphablePackGroupSlug,
    target_slug: targetPhotoSlug,
  };

  trackEvent("view_image", params);
};

export const trackFaceDetected = (faceCount) => {
  trackEvent("face_detected", { face_detected_count: faceCount });
};

export const trackFaceFoundAction = (action) => {
  if (!TRACK_FOUND_ACTIONS.includes(action)) {
    jjLogger.logError(`trackFaceFoundAction: Invalid action: ${action}`);
    return;
  }

  trackEvent("face_found_action", { face_found_action: action });
};

const trackEventGotEveryone = (gotEveryone, savedFacesCount) => {
  trackEvent("got_everyone", { got_everyone: gotEveryone, saved_faces_count: savedFacesCount });
};

export const trackEventGotEveryoneYes = (savedFacesCount) => {
  trackEventGotEveryone("yes", savedFacesCount);
};

export const trackEventGotEveryoneNo = (savedFacesCount) => {
  trackEventGotEveryone("no", savedFacesCount);
};

const trackLoginOrSignUpSuccess = (event, method, inFlow, templateGroup) => {
  if (!TRACK_LOGIN_AND_SIGNUP_METHODS.includes(method)) {
    jjLogger.logError(`trackLoginOrSignUpSuccess: Invalid method: ${method}`);
    return;
  }

  const params = { method };

  if (inFlow) {
    params.item_id = TemplateGroupPresenter.id(templateGroup);
    params.content_type = TemplateGroupPresenter.templateGroupType(templateGroup);
  }

  trackEvent(event, params);
};

export const trackLoginSuccess = (method, inFlow, templateGroup) => {
  trackLoginOrSignUpSuccess("login", method, inFlow, templateGroup);
};

export const trackSignUpSuccess = (method, inFlow, templateGroup) => {
  trackLoginOrSignUpSuccess("sign_up", method, inFlow, templateGroup);
};

export const trackClickSignUp = (method) => {
  trackEvent("click_signup", { method });
};

export const trackViewLogin = () => {
  trackEvent("view_login", {
    content_type: getContentTypeInFlow(),
  });
};

export const trackViewPaygate = () => {
  trackEvent("view_paygate", {
    content_type: getContentTypeInFlow(),
  });
};

export const trackLoginClick = (method) => {
  if (!isMethodValid(method)) {
    jjLogger.logError(`trackLoginClick: Invalid method: ${method}`);
    return;
  }

  trackEvent("click_login", {
    content_type: getContentTypeInFlow(),
    method,
  });
};

export const trackPurchaseSuccess = (method, inFlow, offer, currentUser, template, templateGroup) => {
  const subscription = UserPresenter.activeSubscription(currentUser);
  if (isNil(subscription) || isNil(offer)) {
    jjLogger.logError(
      `trackPurchaseSuccess: Required params not defined. Subscription: ${JSON.stringify(
        subscription,
      )} Offer: ${JSON.stringify(offer)}`,
    );
    return;
  }

  if (inFlow && (isNil(template) || isNil(templateGroup))) {
    jjLogger.logError(
      `trackPurchaseSuccess: Required params not defined. Template: ${JSON.stringify(
        template,
      )} TemplateGroup: ${JSON.stringify(templateGroup)}`,
    );
    return;
  }

  let type = null;
  let item = { item_id: "out-of-flow" };

  if (inFlow) {
    type = TemplateGroupPresenter.templateGroupType(templateGroup);
    item = TemplatePresenter.analyticsItem(template);
  }

  const params = {
    currency: OfferPresenter.currency(offer),
    transaction_id: SubscriptionPresenter.id(subscription),
    value: OfferPresenter.price(offer),
    affiliation: method,
    coupon: OfferPresenter.id(offer),
    content_type: type,
    items: [item],
    inFlow,
  };

  trackEvent("purchase", params);
};

export const trackSearch = (searchTerm) => {
  trackEvent("search", {
    search_term: searchTerm,
  });
};

export const trackShare = (method, templateGroup, additionalParams = {}) => {
  if (!TRACK_SHARE_METHODS.includes(method)) {
    jjLogger.logError(`trackShare: Invalid method: ${method}`);
    return;
  }

  const type = TemplateGroupPresenter.templateGroupType(templateGroup);
  const id = TemplateGroupPresenter.id(templateGroup);

  if (isNil(type) || isNil(id)) {
    jjLogger.logError(
      `trackShare: required params can't be empty. content_type: ${type}. item_id: ${id} templateGroup: ${JSON.stringify(
        templateGroup,
      )}`,
    );
    return;
  }

  const params = {
    method,
    content_type: type,
    item_id: id,
    ...additionalParams,
  };

  trackEvent("share", params);
};

export const trackMorphedPhotoShare = (method, morphedPhoto, templateGroup) => {
  const targetSlug = MorphedPhotoPresenter.morphableTargetPhotoSlug(morphedPhoto);

  trackShare(method, templateGroup, { target_slug: targetSlug });
};

export const trackViewImageAction = (action, morphedPhoto, templateGroupSlug) => {
  if (!TRACK_VIEW_IMAGE_ACTIONS.includes(action)) {
    jjLogger.logError(`trackViewImageAction: Invalid action: ${action}`);
    return;
  }

  const targetSlug = MorphedPhotoPresenter.morphableTargetPhotoSlug(morphedPhoto);

  if (isNil(targetSlug) || isNil(templateGroupSlug)) {
    jjLogger.logError(
      `trackViewImageAction: required params can't be empty. target_slug: ${targetSlug} templateGroupSlug: ${templateGroupSlug}`,
    );
    return;
  }

  trackEvent("view_image_action", { action, item_id: templateGroupSlug, target_slug: targetSlug });
};

export const trackFaceConfirmation = (templateGroupSlug) => {
  if (isNil(templateGroupSlug)) {
    jjLogger.logError(`trackPackGenerated: required params can't be empty. item_id: ${templateGroupSlug}`);
    return;
  }

  trackEvent("face_confirmation", {
    item_id: templateGroupSlug,
  });
};

export const trackFaceConfirmationAction = (action, templateGroup) => {
  if (!TRACK_MORPHABLE_PACK_CONFIRMATION_ACTIONS.includes(action)) {
    jjLogger.logError(`trackFaceConfirmationAction: Invalid action: ${action}`);
    return;
  }

  const type = TemplateGroupPresenter.templateGroupType(templateGroup);
  const id = TemplateGroupPresenter.id(templateGroup);

  if (isNil(type) || isNil(id)) {
    jjLogger.logError(
      `trackFaceConfirmationAction: required params can't be empty. content_type: ${type}. item_id: ${id} templateGroup: ${JSON.stringify(
        templateGroup,
      )}`,
    );
    return;
  }

  trackEvent("face_confirmation_action", {
    content_type: type,
    item_id: id,
    action,
  });
};

export const trackFaceConfirmationSkinColorAction = (skinColor, templateGroup) => {
  const actionKey = MORPHABLE_PACK_CONFIRMATION_ACTIONS_KEYS[skinColor];
  const action = MORPHABLE_PACK_CONFIRMATION_ACTIONS[actionKey];

  trackFaceConfirmationAction(action, templateGroup);
};

export const trackFaceConfirmationUseAnotherFace = (templateGroup) => {
  trackFaceConfirmationAction(MORPHABLE_PACK_CONFIRMATION_ACTIONS.USE_ANOTHER_FACE, templateGroup);
};

export const trackPackGenerated = (templateGroupSlug) => {
  const type = TEMPLATE_GROUP_TYPES.starringYouAI;
  const id = templateGroupSlug;

  if (isNil(id)) {
    jjLogger.logError(`trackPackGenerated: required params can't be empty. content_type: ${type}. item_id: ${id}`);
    return;
  }

  trackEvent("pack_generated", {
    content_type: type,
    item_id: id,
  });
};

export const trackFacePreviewView = (templateGroup, morphedPhoto) => {
  const type = TemplateGroupPresenter.templateGroupType(templateGroup);
  const id = TemplateGroupPresenter.id(templateGroup);
  const targetSlug = MorphedPhotoPresenter.morphableTargetPhotoSlug(morphedPhoto);

  if (isNil(type) || isNil(id)) {
    jjLogger.logError(
      `trackFacePreviewView: required params can't be empty. content_type: ${type}. item_id: ${id} templateGroup: ${JSON.stringify(
        templateGroup,
      )}`,
    );
    return;
  }

  trackEvent("face_preview_view", {
    content_type: type,
    item_id: id,
    target_slug: targetSlug,
  });
};

const trackFacePreviewViewAction = (action, templateGroup) => {
  if (!TRACK_MORPHABLE_PACK_PREVIEW_ACTIONS.includes(action)) {
    jjLogger.logError(`trackFacePreviewViewAction: Invalid action: ${action}`);
    return;
  }

  const type = TemplateGroupPresenter.templateGroupType(templateGroup);
  const id = TemplateGroupPresenter.id(templateGroup);

  if (isNil(type) || isNil(id)) {
    jjLogger.logError(
      `trackFacePreviewViewAction: required params can't be empty. content_type: ${type}. item_id: ${id} templateGroup: ${JSON.stringify(
        templateGroup,
      )}`,
    );
    return;
  }

  trackEvent("face_preview_view_action", {
    content_type: type,
    item_id: id,
    action,
  });
};

export const trackFacePreviewViewActionGenerate = (templateGroup) => {
  trackFacePreviewViewAction(MORPHABLE_PACK_PREVIEW_ACTIONS.GENERATE, templateGroup);
};

export const trackFacePreviewViewActionChangeSkin = (templateGroup) => {
  trackFacePreviewViewAction(MORPHABLE_PACK_PREVIEW_ACTIONS.CHANGE_SKIN, templateGroup);
};

export const trackFacePreviewViewActionChangePhoto = (templateGroup) => {
  trackFacePreviewViewAction(MORPHABLE_PACK_PREVIEW_ACTIONS.CHANGE_PHOTO, templateGroup);
};

const trackInviteShare = (method, invite, templateGroup) => {
  const action = InvitePresenter.trackShareAction(invite);
  const count = InvitePresenter.guestsWithEmail(invite).length;
  const isInviteFirstShare = InvitePresenter.isDraft(invite);
  const additionalParams = {};

  if (action && !TRACK_SHARE_ACTIONS.includes(action)) {
    jjLogger.logError(`trackShare: Invalid action: ${action}`);
    return;
  }

  additionalParams.action = action;

  if ([SHARE_METHODS.PUBLIC_LINK, SHARE_METHODS.GUEST_LINK].includes(method) && count > 0)
    additionalParams.count = count;

  if (!isNil(isInviteFirstShare) && [SHARE_METHODS.EMAIL, SHARE_METHODS.PUBLIC_LINK].includes(method))
    additionalParams.first_send = isInviteFirstShare;

  trackShare(method, templateGroup, additionalParams);
};

export const trackInvitePublicLinkShare = (invite, templateGroup) => {
  trackInviteShare(SHARE_METHODS.PUBLIC_LINK, invite, templateGroup);
};

export const trackInviteGuestLinkShare = (invite, templateGroup) => {
  trackInviteShare(SHARE_METHODS.GUEST_LINK, invite, templateGroup);
};

export const trackInviteEmailShare = (invite, templateGroup) => {
  trackInviteShare(SHARE_METHODS.EMAIL, invite, templateGroup);
};

export const trackSelectContent = (templateGroup) => {
  const type = TemplateGroupPresenter.templateGroupType(templateGroup);
  const id = TemplateGroupPresenter.id(templateGroup);

  if (isNil(type) || isNil(id)) {
    jjLogger.logError(
      `trackSelectContent: required params can't be empty. content_type: ${type}. item_id: ${id} templateGroup: ${JSON.stringify(
        templateGroup,
      )}`,
    );
    return;
  }

  trackEvent("select_content", {
    content_type: type,
    item_id: id,
  });
};

export const trackMakeCreationSuccess = (make, templateGroup) => {
  const template = MakePresenter.template(make);
  const id = TemplateGroupPresenter.id(templateGroup);
  const status = MakePresenter.status(make);
  const templateGroupType = TemplateGroupPresenter.templateGroupType(templateGroup);

  if (isNil(id) || isNil(status) || isNil(templateGroupType)) {
    jjLogger.logError(
      `trackMakeCreationSuccess: item_id or make status or templateGroupType information can't be empty. template: ${JSON.stringify(
        template,
      )} make: ${JSON.stringify(make)}`,
    );
    return;
  }

  trackEvent("make_creation_success", {
    item_id: id,
    make_status: status,
    content_type: templateGroupType,
  });
};

export const trackMakeCreationError = (error) => {
  if (isNil(error)) {
    jjLogger.logError(`trackMakeCreationError: error message can't be empty. Error: ${error}`);
    return;
  }

  trackEvent("make_creation_error", {
    make_creation_error_value: error,
  });
};

export const trackMakeViewSuccess = (rendererType) => {
  if (isNil(rendererType)) {
    jjLogger.logError(`trackMakeViewSuccess: renderer type message can't be empty.`);
    return;
  }

  trackEvent("make_view_success", {
    renderer: rendererType,
  });
};

export const trackMakeViewError = (rendererType) => {
  if (isNil(rendererType)) {
    jjLogger.logError(`trackMakeViewError: renderer type can't be empty.`);
    return;
  }

  trackEvent("make_view_error", {
    renderer: rendererType,
  });
};

export const trackTemplateView = (templateGroup) => {
  if (isNil(templateGroup)) {
    jjLogger.logError(
      `trackTemplateView: required params can't be empty. templateGroup: ${JSON.stringify(templateGroup)}`,
    );
    return;
  }

  const template = TemplateGroupPresenter.template(templateGroup);
  if (isNil(template)) {
    jjLogger.logError(
      `trackTemplateView: template group does not have template. templateGroup: ${JSON.stringify(templateGroup)}`,
    );

    return;
  }

  trackEvent("view_item", {
    content_type: TemplateGroupPresenter.templateGroupType(templateGroup),
    items: [TemplatePresenter.analyticsItem(template)],
  });
};

export const trackSelectItem = (itemListInfo, item) => {
  if (isNil(itemListInfo) || isNil(item)) {
    jjLogger.logError(
      `trackSelectItem: required params can't be empty. itemListInfo: ${JSON.stringify(
        itemListInfo,
      )} item: ${JSON.stringify(item)} `,
    );
    return;
  }

  const { itemListId, itemListName, indexPosition } = itemListInfo;
  const parameters = {
    item_list_id: itemListId,
    item_list_name: itemListName,
    content_type: TemplateGroupPresenter.templateGroupType(item),
    items: [TemplateGroupPresenter.analyticsItem(item)],
  };

  if (indexPosition) parameters.index_position = indexPosition;

  trackEvent("select_item", parameters);
};

export const handlerSelectItemEvent = (item, category, homePage = null, extraParams = {}) => {
  let itemListInfo;
  const categoryPath = appRoutes.categoryPath(CategoryPresenter.id(category));
  const inviteCategoryPath = appRoutes.invitePath(CategoryPresenter.id(category));
  const homePath = appRoutes.rootPath();
  const searchPath = appRoutes.searchPath();
  const { pathname, search } = window.location;
  const queryParams = new URLSearchParams(search);

  switch (true) {
    case category && (categoryPath === pathname || inviteCategoryPath === pathname):
      itemListInfo = {
        itemListId: CategoryPresenter.id(category),
        itemListName: CategoryPresenter.displayName(category),
        indexPosition: extraParams?.indexPosition || null,
      };
      break;
    case homePage && homePath === pathname:
      itemListInfo = {
        itemListId: HomePagePresenter.id(homePage),
        itemListName: HomePagePresenter.name(homePage),
      };
      break;
    case searchPath === pathname:
      if (queryParams.has("term")) {
        const term = `term=${queryParams.get("term")}`;
        itemListInfo = {
          itemListId: term,
          itemListName: term,
        };
      }
      break;
    case appRoutes.invitesPath() === pathname:
      itemListInfo = {
        itemListId: "/invites/browse",
        itemListName: "Invites Landing Page",
      };
      break;
    case appRoutes.starringYouAIPath() === pathname:
      itemListInfo = {
        itemListId: "/ai-photos",
        itemListName: "Starring You AI Landing Page",
      };
      break;
    case !isNil(extraParams?.page):
      itemListInfo = {
        itemListId: PagePresenter.id(extraParams?.page),
        itemListName: PagePresenter.name(extraParams?.page),
        indexPosition: extraParams?.indexPosition || null,
      };
      break;
    default:
      break;
  }

  trackSelectItem(itemListInfo, item);
};

export const track404 = (url) => {
  if (isNil(url)) {
    jjLogger.logError(`track404: url can't be empty. url: ${url}`);
    return;
  }

  trackEvent("404", {
    404: url,
  });
};

export const trackEditSubscriptionBilling = () => {
  trackEvent("edit_subscription_billing", {
    billing_info: "billing info updated",
  });
};

export const trackEditSubscriptionSuccess = (action) => {
  if (isNil(action) || !TRACK_EDIT_SUBSCRIPTION_ACTIONS.includes(action)) {
    jjLogger.logError(`trackEditSubscriptionSuccess: Invalid keep renewal action: ${action}`);
    return;
  }

  trackEvent("edit_subscription_success", {
    keep_renewal: action,
  });
};

export const trackSubscriptionFeedback = (answer) => {
  if (isNil(answer)) {
    jjLogger.logError(`trackSubscriptionFeedback: Invalid feedback: ${answer}`);
    return;
  }

  trackEvent("feedback", {
    feedback: answer,
  });
};

export const trackCalendarHit = (occasion) => {
  if (isNil(occasion)) {
    jjLogger.logError(`trackCalendarHit: Invalid occasion: occasion should not be nil or empty`);
    return;
  }

  trackEvent("calendar_hit", {
    occasion,
  });
};

export const trackCalendarDiscovery = (action) => {
  if (isNil(action)) {
    jjLogger.logError("trackCalendarDiscovery: Invalid action: action should not be nil or empty");
    return;
  }

  trackEvent("calendar_discovery", {
    calendar_discovery: action,
  });
};

const trackPersonSuccessEvent = (functionName, eventType, person) => {
  if (isNil(person)) {
    jjLogger.logError(`${functionName}: Required params not defined. Person: ${JSON.stringify(person)}`);
    return;
  }

  const params = {};
  if (PersonPresenter.relation(person)) params.relation = PersonPresenter.relation(person);
  if (PersonPresenter.birthday(person)) params.birthday = "yes";
  if (PersonPresenter.anniversary(person)) params.anniversary = "yes";

  trackEvent(eventType, params);
};

export const trackPersonCreationSuccess = (person) => {
  trackPersonSuccessEvent("trackPersonCreationSuccess", "person_created", person);
};

export const trackPersonUpdateSuccess = (person) => {
  trackPersonSuccessEvent("trackPersonUpdateSuccess", "person_updated", person);
};

export const trackInviteUpdated = (invite, action) => {
  if (isNil(invite) || isNil(action)) {
    jjLogger.logError(
      `trackInviteUpdated: Required params not defined. invite: ${JSON.stringify(invite)} action: ${action}`,
    );
    return;
  }

  if (InvitePresenter.isDraft(invite) || InvitePresenter.isDeleted(invite)) return;

  trackEvent("invite_updated", {
    item_id: InvitePresenter.id(invite),
    action,
  });
};

export const trackInviteCompleted = (oldInvite, newInvite) => {
  if (isNil(oldInvite) || isNil(newInvite)) {
    jjLogger.logError(
      `trackInviteCompleted: Required params not defined. oldInvite: ${JSON.stringify(
        oldInvite,
      )} newInvite: ${JSON.stringify(newInvite)}`,
    );
    return;
  }

  const isOldInviteDataNull = isNil(InvitePresenter.title(oldInvite)) && isNil(InvitePresenter.eventDate(oldInvite));
  const isNewInviteDataPresent =
    !isNil(InvitePresenter.title(newInvite)) && !isNil(InvitePresenter.eventDate(newInvite));

  if (isOldInviteDataNull && isNewInviteDataPresent) trackEvent("invite_completed");
};

export const trackRsvpSuccess = ({ itemId, rsvp, rsvpCount, isPublic }) => {
  const type = isPublic ? "public" : "private";
  trackEvent("recipient_response", { item_id: itemId, rsvp, rsvp_count: rsvpCount, type });
};

export const trackGuestInfoInput = (fields) => {
  if (isNil(fields) || isEmpty(fields) || !Array.isArray(fields)) return;

  const changedFields = fields.join(", ");

  trackEvent("guest_info_input", {
    input_field: changedFields,
  });
};

export const trackInviteEventInfo = (invite) => {
  if (isNil(invite)) {
    jjLogger.logError("trackInviteUpdated: Required invite param not defined.");
    return;
  }

  trackEvent("event_info_input", {
    input_field: InvitePresenter.populatedFieldNames(invite),
  });
};

const trackVideoPlayback = (event, video, templateGroup, percentage) => {
  if (isNil(event) || isNil(video) || isNil(templateGroup) || isNil(percentage)) {
    jjLogger.logError(`trackVideoPlayback: Required params not defined.
      event: ${JSON.stringify(event)}
      video: ${JSON.stringify(video)}
      templateGroup: ${JSON.stringify(templateGroup)}
      percentage: ${percentage}`);
    return;
  }

  const { currentTime, duration, loop } = video;
  const { pathname } = window.location;
  const params = {
    video_current_time: currentTime,
    video_duration: duration,
    video_percent: percentage,
    video_provider: TemplateGroupPresenter.templateGroupType(templateGroup),
    video_title: TemplateGroupPresenter.name(templateGroup),
    looping_video: loop,
    video_url: pathname,
  };

  trackEvent(event, params);
};

export const trackVideoProgress = (video, percentage, templateGroup) => {
  trackVideoPlayback("video_progress", video, templateGroup, percentage);
};

export const trackVideoStart = (video, templateGroup) => {
  trackVideoPlayback("video_start", video, templateGroup, 0);
};

export const trackVideoComplete = (video, templateGroup) => {
  trackVideoPlayback("video_complete", video, templateGroup, 100);
};

export const trackClickSignUpEmail = () => {
  trackEvent("click_signup", { method: "signup-email" });
};

export const trackClickSignUpFacebook = () => {
  trackEvent("click_signup", { method: "signup-facebook" });
};

export const trackViewSignUp = () => {
  const contentType = getContentTypeInFlow();

  trackEvent("view_sign_up", {
    content_type: contentType,
  });
};
